// app/frontend/components/inbox/PendingAttachment.tsx

import { useEffect, useMemo } from 'react'
import { FileText, RotateCw, X } from 'lucide-react'
import { formatFileSize } from '@/lib/fileUpload'
import { cn } from '@/lib/utils'
import type { PendingFile } from '@/hooks/useFileUpload'

interface PendingAttachmentProps {
  pendingFile: PendingFile
  onRemove?: () => void
  onRetry?: () => void
}

export function PendingAttachment({ pendingFile, onRemove, onRetry }: PendingAttachmentProps) {
  const { file, status, progress, error } = pendingFile

  const previewUrl = useMemo(
    () => (file.type.startsWith('image/') ? URL.createObjectURL(file) : null),
    [file]
  )

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  return (
    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
      {previewUrl ? (
        <img src={previewUrl} alt={file.name} className="w-10 h-10 object-cover rounded" />
      ) : (
        <FileText className="size-10 text-muted-foreground" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{file.name}</div>
        <div className={cn('text-xs', status === 'error' ? 'text-destructive' : 'text-muted-foreground')}>
          {status === 'uploading' ? `Uploading ${progress}%` :
           status === 'error' ? error :
           formatFileSize(file.size)}
        </div>
        {status === 'uploading' && (
          <div className="h-1 bg-muted-foreground/20 rounded mt-1 overflow-hidden">
            <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      {onRetry && status === 'error' && pendingFile.retryable && (
        <button
          type="button"
          onClick={onRetry}
          className="p-1 hover:bg-background rounded cursor-pointer"
          title="Retry upload"
        >
          <RotateCw className="size-4 text-muted-foreground" />
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1 hover:bg-background rounded cursor-pointer"
          title="Remove attachment"
        >
          <X className="size-4 text-muted-foreground" />
        </button>
      )}
    </div>
  )
}
